import { Api } from "@/api/Api";
import type { FilterAndSorterFormTypes } from "@/dashboard/types";
import { genericSearch } from "@/utility/genericSearch";
import { genericSort } from "@/utility/genericSort";
import type {
  SlimAccount,
  SlimAccountV2,
  SlimProfile,
  V2UpdateAccountRequest,
} from "@mm/metx-workbench.meteomatics.com";
import type { UseFormSetValue } from "react-hook-form";

/**
 * Filters and sorts profiles for the dashboard list.
 *
 * Search behavior:
 * - The query is trimmed before matching.
 * - A profile matches when either the dashboard title OR any tab title matches.
 * - Tab titles can be null and are ignored safely.
 *
 * Result behavior:
 * - Optional tag filtering is applied after search.
 * - Matched profiles are split into pinned and unpinned groups.
 * - Each group is sorted independently, then combined with pinned first.
 */
export const processProfiles = (
  profiles: SlimProfile[],
  query: string,
  tags: string[],
  sorter: FilterAndSorterFormTypes["sort"],
  pinnedIds: Set<number>,
): SlimProfile[] => {
  const normalizedQuery = query.trim();

  // 1. Filter all profiles using the generic search utility
  let filtered = normalizedQuery
    ? profiles.filter((profile) => {
        const profileTitleMatch = genericSearch<SlimProfile>(profile, ["title"], normalizedQuery);
        const tabTitleMatch = (profile.tabs ?? []).some((tab) => genericSearch(tab, ["title"], normalizedQuery));
        return profileTitleMatch || tabTitleMatch;
      })
    : profiles;

  filtered = filtered.filter((profile: SlimProfile) => {
    if (tags.length === 0) {
      return true;
    }
    return profile.tags?.some((tag) => tags.includes(tag));
  });
  // 2. Partition the filtered results into pinned and unpinned arrays
  const pinnedProfiles: SlimProfile[] = [];
  const unpinnedProfiles: SlimProfile[] = [];

  for (const profile of filtered) {
    if (pinnedIds.has(profile.id)) {
      pinnedProfiles.push(profile);
    } else {
      unpinnedProfiles.push(profile);
    }
  }

  // 3. Sort each partition independently using the generic sort utility
  const sortedPinned = pinnedProfiles.sort((a, b) => genericSort<SlimProfile>(a, b, sorter));
  const sortedUnpinned = unpinnedProfiles.sort((a, b) => genericSort<SlimProfile>(a, b, sorter));

  // 4. Return the combined result, with pinned items first
  return [...sortedPinned, ...sortedUnpinned];
};

export const updateDashboardPreferences = (
  account: SlimAccountV2,
  updates: {
    pinnedIds?: Set<number>;
    sorting?: FilterAndSorterFormTypes["sort"];
  },
) => {
  const currentPrefs = account.preferences?.dashboard || {};

  const updateReq: V2UpdateAccountRequest = {
    accountId: account.id,
    accountUpdate: {
      preferences: {
        dashboard: {
          ...currentPrefs,
          ...(updates.pinnedIds && { pinned_profiles: Array.from(updates.pinnedIds) }),
          ...(updates.sorting && {
            sorting_option: updates.sorting.property,
            sorting_direction: updates.sorting.isDescending ? "desc" : "asc",
          }),
        },
      },
    },
  };

  return Api.account
    .v2UpdateAccount(updateReq)
    .then((updatedAccount: SlimAccount) => {
      return updatedAccount;
    })
    .catch((error) => {
      console.error("Failed to update dashboard preferences:", error);
      throw error;
    });
};

export type SortItem = {
  id: string;
  label: string;
  iconName: string;
  onSelect: () => void;
};

export function buildSortMenuItems(
  filterOptions: Record<string, string>,
  activeSort: FilterAndSorterFormTypes["sort"] | undefined,
  setValue: UseFormSetValue<FilterAndSorterFormTypes>,
  onSortChange?: (sort: FilterAndSorterFormTypes["sort"]) => void,
): SortItem[] {
  return Object.entries(filterOptions).map(([key, label]) => {
    const property = key as keyof Partial<SlimProfile>;
    const isActive = activeSort?.property === property;
    const iconName = isActive ? (activeSort?.isDescending ? "arrow_downward" : "arrow_upward") : "swap_vert";

    return {
      id: String(key),
      label: String(label),
      iconName,
      onSelect: () => {
        const isDescending = isActive ? !activeSort?.isDescending : activeSort?.isDescending ?? true;
        const newSort = { property, isDescending };
        setValue("sort", newSort, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
        onSortChange?.(newSort);
      },
    };
  });
}
